import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { Bell, AlertTriangle, Clock, ChevronRight, Check, MessageSquare } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const TodaysCommunications = ({ basePath = '/worker' }) => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const response = await api.get('/communications/summary/today');
        setSummary(response.data);
      } catch (error) {
        console.error('Failed to load communications summary');
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [api]);

  // Auto-mark all displayed comms as read so they disappear next time
  useEffect(() => {
    if (!summary || !user?.id) return;
    const allComms = [...(summary.important || []), ...(summary.expiring_soon || []), ...(summary.general || [])];
    const unacked = allComms.filter(c => !c.acknowledged_by?.includes(user.id));
    if (unacked.length === 0) return;
    const markRead = async () => {
      for (const c of unacked) {
        try { await api.post('/communications/' + c.id + '/acknowledge'); } catch(e) {}
      }
      // Refresh to update the count
      try {
        const res = await api.get('/communications/summary/today');
        setSummary(res.data);
      } catch(e) {}
    };
    markRead();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary?.important?.length, summary?.expiring_soon?.length, summary?.general?.length]);

  const handleReadAndNavigate = async (commId) => {
    try {
      await api.post('/communications/' + commId + '/acknowledge');
      const response = await api.get('/communications/summary/today');
      setSummary(response.data);
    } catch (e) {}
    navigate(basePath + '/communications');
  };

  const handleAcknowledge = async (commId, e) => {
    e.stopPropagation();
    try {
      await api.post(`/communications/${commId}/acknowledge`);
      // Refresh summary
      const response = await api.get('/communications/summary/today');
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to acknowledge');
    }
  };

  if (loading) {
    return (
      <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Today's Key Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-4">
            <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Filter out communications the user has already acknowledged
  const isAcked = (comm) => comm.acknowledged_by?.includes(user?.id);
  const unackedImportant = summary?.important?.filter(c => !isAcked(c)) || [];
  const unackedExpiring = summary?.expiring_soon?.filter(c => !isAcked(c)) || [];
  const unackedGeneral = summary?.general?.filter(c => !isAcked(c)) || [];

  if (!summary || (unackedImportant.length === 0 && unackedExpiring.length === 0 && unackedGeneral.length === 0)) {
    return null; // Don't show if no unacknowledged communications
  }

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Bell className="h-4 w-4 text-primary" />
            Today's Key Information
            {summary.total_unacknowledged > 0 && (
              <Badge variant="destructive" className="text-xs">
                {summary.total_unacknowledged} unread
              </Badge>
            )}
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate(`${basePath}/communications`)}
          >
            View All
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Important Communications */}
        {unackedImportant.map(comm => {
          const isAcknowledged = comm.acknowledged_by?.includes(user?.id);
          return (
            <div
              key={comm.id}
              className={cn(
                "p-3 rounded-lg border cursor-pointer transition-all hover:shadow-sm",
                "border-red-500/30 bg-red-500/10"
              )}
              onClick={() => handleReadAndNavigate(comm.id)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0" />
                    <span className="font-medium text-sm truncate">{comm.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">{comm.message}</p>
                </div>
                {comm.requires_acknowledgment && !isAcknowledged && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="flex-shrink-0"
                    onClick={(e) => handleAcknowledge(comm.id, e)}
                  >
                    <Check className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}

        {/* Expiring Soon */}
        {unackedExpiring.map(comm => (
          <div
            key={comm.id}
            className={cn(
              "p-3 rounded-lg border cursor-pointer transition-all hover:shadow-sm",
              "border-amber-500/30 bg-amber-500/10"
            )}
            onClick={() => handleReadAndNavigate(comm.id)}
          >
            <div className="flex items-start gap-2">
              <Clock className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <span className="font-medium text-sm">{comm.title}</span>
                <p className="text-xs text-muted-foreground line-clamp-1">{comm.message}</p>
                <p className="text-xs text-amber-600 mt-1">
                  Expires: {format(new Date(comm.expiry_date), 'MMM d, h:mm a')}
                </p>
              </div>
            </div>
          </div>
        ))}

        {/* General (only show if no important/expiring) */}
        {unackedImportant.length === 0 && unackedExpiring.length === 0 && unackedGeneral.slice(0, 3).map(comm => (
          <div
            key={comm.id}
            className={cn(
              "p-3 rounded-lg border cursor-pointer transition-all hover:shadow-sm",
              "border-border/50 bg-muted/30"
            )}
            onClick={() => handleReadAndNavigate(comm.id)}
          >
            <div className="flex items-start gap-2">
              <MessageSquare className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <span className="font-medium text-sm">{comm.title}</span>
                <p className="text-xs text-muted-foreground line-clamp-1">{comm.message}</p>
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default TodaysCommunications;
